import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, MapPin, Coins, PenSquare, Stethoscope, GraduationCap, Headphones, Users,
  Check, Minus, Search, Download, ArrowLeft, Lock, Sparkles, AlertTriangle
} from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

// Roles definitions (technical, exhaustive)
const ROLES = [
  { key: 'super_admin', label: 'Super Admin', icon: Shield, color: '#dc2626', bg: '#fef2f2' },
  { key: 'regional', label: 'Resp. Régional', icon: MapPin, color: '#2563eb', bg: '#eff6ff' },
  { key: 'tresorier', label: 'Trésorier', icon: Coins, color: '#d97706', bg: '#fffbeb' },
  { key: 'auteur', label: 'Auteur', icon: PenSquare, color: '#16a34a', bg: '#f0fdf4' },
  { key: 'ophthalmologist', label: 'Ophtalmo.', icon: Stethoscope, color: '#35AFE4', bg: '#eaf6fc' },
  { key: 'intern', label: 'Interne', icon: GraduationCap, color: '#059669', bg: '#ecfdf5' },
];

// L = Lire, W = Écrire/Créer, M = Modérer, R = Régional uniquement, P = Permission optionnelle
const CAP = (level) => ({ level });

// Matrice : level peut être 'full' | 'region' | 'own' | 'read' | 'opt' | 'none'
const SECTIONS = [
  {
    name: 'Pages & Contenus du site',
    rows: [
      { feature: "Page d'accueil (CMS)", super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Pages institutionnelles', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Bureau national', super_admin: 'full', regional: 'read', tresorier: 'read', auteur: 'read', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Bureaux régionaux', super_admin: 'full', regional: 'region', tresorier: 'none', auteur: 'none', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Pages régionales', super_admin: 'full', regional: 'region', tresorier: 'none', auteur: 'none', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Actualité LinkedIn', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'read', intern: 'read' },
    ]
  },
  {
    name: 'Formations & Articles scientifiques',
    rows: [
      { feature: 'Formations (CRUD)', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'own', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Articles scientifiques', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'own', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Blog / Actualités', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'own', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Événements / Congrès', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'own', ophthalmologist: 'read', intern: 'read' },
      { feature: 'Replays vidéos', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'own', ophthalmologist: 'read', intern: 'read' },
    ]
  },
  {
    name: 'Espace Patients',
    rows: [
      { feature: 'Contenu patients (CMS)', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Articles publics patients', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'read', intern: 'read' },
    ]
  },
  {
    name: 'Membres & Adhésions',
    rows: [
      { feature: 'Liste des membres', super_admin: 'full', regional: 'region', tresorier: 'read', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Validation des adhésions', super_admin: 'full', regional: 'region', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Modification des rôles', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Rappels d\'adhésion', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Fiches annuaire (modération)', super_admin: 'full', regional: 'region', tresorier: 'none', auteur: 'none', ophthalmologist: 'own', intern: 'none' },
      { feature: 'Export CSV des membres', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
    ]
  },
  {
    name: 'Finances',
    rows: [
      { feature: 'Cotisations & Paiements', super_admin: 'full', regional: 'region', tresorier: 'read', auteur: 'none', ophthalmologist: 'own', intern: 'none' },
      { feature: 'Reçus fiscaux (génération)', super_admin: 'full', regional: 'opt', tresorier: 'full', auteur: 'none', ophthalmologist: 'own', intern: 'none' },
      { feature: 'Justificatifs HelloAsso', super_admin: 'full', regional: 'region', tresorier: 'read', auteur: 'none', ophthalmologist: 'own', intern: 'none' },
    ]
  },
  {
    name: 'Communauté',
    rows: [
      { feature: 'Forum privé (lecture)', super_admin: 'full', regional: 'full', tresorier: 'full', auteur: 'full', ophthalmologist: 'full', intern: 'full' },
      { feature: 'Forum privé (publication)', super_admin: 'full', regional: 'full', tresorier: 'full', auteur: 'full', ophthalmologist: 'full', intern: 'full' },
      { feature: 'Modération du forum', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Élections nationales', super_admin: 'full', regional: 'read', tresorier: 'read', auteur: 'read', ophthalmologist: 'read', intern: 'none' },
      { feature: 'Élections régionales', super_admin: 'full', regional: 'region', tresorier: 'read', auteur: 'read', ophthalmologist: 'read', intern: 'none' },
      { feature: 'Tickets support (création)', super_admin: 'full', regional: 'full', tresorier: 'full', auteur: 'full', ophthalmologist: 'full', intern: 'full' },
      { feature: 'Tickets support (résolution)', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
    ]
  },
  {
    name: 'Configuration & Sécurité',
    rows: [
      { feature: 'Bannières publicitaires', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Notifications globales', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Statistiques & Analytics', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Paramètres globaux', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Notes de version', super_admin: 'full', regional: 'read', tresorier: 'read', auteur: 'read', ophthalmologist: 'none', intern: 'none' },
      { feature: 'Audit d\'impersonation', super_admin: 'full', regional: 'none', tresorier: 'none', auteur: 'none', ophthalmologist: 'none', intern: 'none' },
    ]
  },
];

const LEVEL_META = {
  full:   { label: 'Complet',   short: 'C', color: '#16a34a', bg: '#dcfce7', tooltip: 'Lecture + Création + Modification + Suppression' },
  region: { label: 'Régional',  short: 'R', color: '#2563eb', bg: '#dbeafe', tooltip: 'Limité à sa région (managed_region)' },
  own:    { label: 'Personnel', short: 'P', color: '#7c3aed', bg: '#ede9fe', tooltip: 'Limité à ses propres contributions' },
  read:   { label: 'Lecture',   short: 'L', color: '#0891b2', bg: '#cffafe', tooltip: 'Lecture seule' },
  opt:    { label: 'Optionnel', short: 'O', color: '#d97706', bg: '#fef3c7', tooltip: 'Permission optionnelle (toggle Admin requis)' },
  none:   { label: '—',         short: '—', color: '#9ca3af', bg: '#f3f4f6', tooltip: 'Aucun accès' },
};

const Cell = ({ level }) => {
  const meta = LEVEL_META[level] || LEVEL_META.none;
  return (
    <td className="px-3 py-2.5 text-center">
      <span
        className="inline-flex items-center justify-center min-w-[26px] h-[22px] rounded-md text-[11px] font-bold border"
        style={{ color: meta.color, backgroundColor: meta.bg, borderColor: meta.color + '40' }}
        title={meta.tooltip}
        data-testid={`cell-${level}`}>
        {level === 'none' ? <Minus size={12} /> : meta.short}
      </span>
    </td>
  );
};

const SectionRows = ({ section }) => (
  <>
    <tr className="bg-[#1e3a5f]/5 border-y border-[#1e3a5f]/10">
      <td colSpan={ROLES.length + 1} className="px-4 py-2 text-[11px] font-bold text-[#1e3a5f] uppercase tracking-wider">
        {section.name}
      </td>
    </tr>
    {section.rows.map((row, ri) => (
      <tr key={ri} className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
        <td className="px-4 py-2.5 text-[13px] text-gray-800 font-medium sticky left-0 bg-white z-10 hover:bg-gray-50/40">
          {row.feature}
        </td>
        {ROLES.map(role => (
          <Cell key={role.key} level={row[role.key] || 'none'} />
        ))}
      </tr>
    ))}
  </>
);

const RolesPermissionsAdminPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filteredSections = useMemo(() => {
    if (!search.trim()) return SECTIONS;
    const q = search.toLowerCase();
    return SECTIONS.map(s => ({
      ...s,
      rows: s.rows.filter(r => r.feature.toLowerCase().includes(q) || s.name.toLowerCase().includes(q)),
    })).filter(s => s.rows.length > 0);
  }, [search]);

  const handleExport = () => {
    const lines = [];
    lines.push('Section,Fonctionnalité,' + ROLES.map(r => r.label).join(','));
    SECTIONS.forEach(s => {
      s.rows.forEach(r => {
        const cells = ROLES.map(role => LEVEL_META[r[role.key] || 'none']?.label || '—');
        lines.push(`"${s.name}","${r.feature}",${cells.map(c => `"${c}"`).join(',')}`);
      });
    });
    const csv = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sfoalc-roles-permissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 py-6 max-w-full" data-testid="admin-roles-permissions">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mb-2" data-testid="back-admin">
              <ArrowLeft size={14} /> Tableau de bord
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-1.5" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Rôles & Permissions
            </h1>
            <p className="text-sm text-gray-600 max-w-2xl">
              Matrice complète des accès par rôle. Référence technique pour les administrateurs.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/roles-permissions')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
              data-testid="view-public-btn">
              <Sparkles size={14} /> Vue publique
            </button>
            <button onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-[#1e3a5f] text-white hover:bg-[#162d4a] transition-colors"
              data-testid="export-csv-btn">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="legend">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">Légende des niveaux d'accès</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LEVEL_META).filter(([k]) => k !== 'none').map(([key, meta]) => (
              <div key={key} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]"
                style={{ backgroundColor: meta.bg, color: meta.color }}>
                <span className="font-bold">{meta.short}</span>
                <span>{meta.label}</span>
                <span className="opacity-60">— {meta.tooltip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une fonctionnalité…"
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/30"
            data-testid="search-input"
          />
        </div>

        {/* Special permissions banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3" data-testid="special-perms">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-bold mb-1">Permissions spéciales (toggles)</p>
            <ul className="space-y-0.5 text-[13px] list-disc ml-4">
              <li><span className="font-semibold">can_generate_receipts</span> — active la génération de reçus fiscaux pour un Responsable régional</li>
              <li><span className="font-semibold">is_support</span> — accorde l'accès à l'impersonation et au support technique avancé</li>
              <li><span className="font-semibold">managed_region</span> — fixe la région opérée par un Responsable régional</li>
            </ul>
          </div>
        </div>

        {/* Matrix */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="permissions-matrix">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">Fonctionnalité</th>
                  {ROLES.map(role => {
                    const Icon = role.icon;
                    return (
                      <th key={role.key} className="text-center px-3 py-3 text-[11px] font-bold uppercase tracking-wider min-w-[110px]">
                        <div className="flex flex-col items-center gap-1.5">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: role.bg }}>
                            <Icon size={15} style={{ color: role.color }} strokeWidth={2.4} />
                          </div>
                          <span style={{ color: role.color }}>{role.label}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredSections.map((section) => (
                  <SectionRows key={section.name} section={section} />
                ))}
                {filteredSections.length === 0 && (
                  <tr>
                    <td colSpan={ROLES.length + 1} className="text-center py-10 text-sm text-gray-400">
                      Aucune fonctionnalité ne correspond à « {search} »
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          Source de vérité : <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">backend/server.py</code> &middot; <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">frontend/components/AdminLayout.js</code>
        </p>
      </div>
    </AdminLayout>
  );
};

export default RolesPermissionsAdminPage;
